import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Phone,
  MapPin,
  Globe,
  FileText,
  Calendar,
  AlertTriangle,
  Wallet,
  ShoppingCart,
  Receipt,
  Store,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { SupplierBrandsCard } from '@/components/trading/supplier-brands-card';
import { SupplierProductsCard } from '@/components/trading/supplier-products-card';
import { trpc } from '@/lib/trpc';
import { cn, formatCurrency, formatDate } from '@/lib/utils';

type CariSupplierType = 'T' | 'S' | 'B';

const PO_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  PARTIALLY_RECEIVED: 'bg-indigo-100 text-indigo-700',
  FULLY_RECEIVED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const INVOICE_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-blue-100 text-blue-700',
  APPROVED: 'bg-indigo-100 text-indigo-700',
  PARTIALLY_PAID: 'bg-amber-100 text-amber-700',
  PAID: 'bg-green-100 text-green-700',
  OVERDUE: 'bg-orange-100 text-orange-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

interface SupplierDetailContentProps {
  cariType: CariSupplierType;
  supplier: any;
}

export function SupplierDetailContent({ cariType, supplier }: SupplierDetailContentProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  // Recent purchase orders (latest 5)
  const { data: poData } = trpc.tradingPurchaseOrder.list.useQuery(
    { supplierId: supplier.id, limit: 5, sortBy: 'orderDate', sortOrder: 'desc', page: 1 },
    { enabled: !!supplier.id }
  );
  const recentPOs = poData?.orders ?? [];
  const totalPOs = poData?.pagination?.total ?? supplier._count?.purchaseOrders ?? 0;

  // Supplier invoices
  const { data: invoices } = trpc.tradingSupplierInvoice.bySupplier.useQuery(
    supplier.id,
    { enabled: !!supplier.id }
  );

  // Sell-side activity: sales orders + invoices we issued TO this vendor
  // (when we sold them stock — distinct from POs/supplier-invoices above
  // which are when we bought from them).
  const { data: salesOrdersData } = trpc.tradingSalesOrder.list.useQuery(
    { tradingSupplierId: supplier.id, limit: 10, page: 1 },
    { enabled: !!supplier.id }
  );
  const salesOrders = salesOrdersData?.orders ?? [];

  const { data: salesInvoicesData } = trpc.tradingInvoice.list.useQuery(
    { tradingSupplierId: supplier.id, limit: 10, page: 1 },
    { enabled: !!supplier.id }
  );
  const salesInvoices = salesInvoicesData?.invoices ?? [];

  // Consignments out to this vendor (the list endpoint returns an
  // unpaginated array — keep this in sync if it grows pagination later).
  const { data: consignmentsData } = trpc.tradingConsignment.list.useQuery(
    { vendorId: supplier.id },
    { enabled: !!supplier.id }
  );
  const consignments = consignmentsData ?? [];

  // Outstanding payables grouped by currency
  const openByCurrency: Record<string, number> = {};
  let overdueCount = 0;
  for (const inv of invoices ?? []) {
    if (inv.balanceDue > 0) {
      openByCurrency[inv.currency] = (openByCurrency[inv.currency] ?? 0) + inv.balanceDue;
      if (inv.isOverdue) overdueCount++;
    }
  }
  const openEntries = Object.entries(openByCurrency);

  const paymentTermsLabels: Record<string, string> = {
    PREPAID: 'Prepaid',
    CREDIT_15: 'Net 15',
    CREDIT_30: 'Net 30',
    CREDIT_60: 'Net 60',
    CREDIT_90: 'Net 90',
  };

  const typeBadgeStyles: Record<CariSupplierType, string> = {
    T: 'bg-orange-100 text-orange-700',
    S: 'bg-purple-100 text-purple-700',
    B: 'bg-green-100 text-green-700',
  };

  const typeLabelKey: Record<CariSupplierType, string> = {
    T: 'cari.supplier',
    S: 'cari.vendor',
    B: 'cari.dealer',
  };

  const creditLimit =
    typeof supplier.creditLimit === 'object'
      ? (supplier.creditLimit as any).toNumber()
      : Number(supplier.creditLimit);

  return (
    <Tabs defaultValue="overview" className="space-y-4">
      <TabsList>
        <TabsTrigger value="overview">{t('customers.tabs.overview')}</TabsTrigger>
        <TabsTrigger value="contacts">{t('customers.tabs.contacts')}</TabsTrigger>
        <TabsTrigger value="orders">{t('purchasing.suppliers.purchaseOrders')}</TabsTrigger>
        <TabsTrigger value="invoices">{t('purchasing.suppliers.invoices')}</TabsTrigger>
        <TabsTrigger value="sales">{t('cari.sellSide')}</TabsTrigger>
        <TabsTrigger value="consignment">{t('cari.consignmentTab')}</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{tCommon('status')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{tCommon('status')}</span>
                <span
                  className={cn(
                    'px-2 py-0.5 text-xs font-medium',
                    supplier.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  )}
                >
                  {supplier.isActive ? tCommon('active') : tCommon('inactive')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t(typeLabelKey[cariType])}</span>
                <span className={`px-2 py-0.5 text-xs font-medium ${typeBadgeStyles[cariType]}`}>
                  {cariType} - {t(typeLabelKey[cariType])}
                </span>
              </div>
              {supplier.paymentTerms && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t('purchasing.suppliers.paymentTerms')}
                  </span>
                  <span className="font-medium">
                    {paymentTermsLabels[supplier.paymentTerms] || supplier.paymentTerms}
                  </span>
                </div>
              )}
              {creditLimit > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t('customers.credit.creditLimit')}
                  </span>
                  <span className="font-medium">
                    {creditLimit.toLocaleString()} {supplier.currency}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('customers.primaryContact')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="font-medium">{supplier.contactName}</div>
              {supplier.contactTitle && (
                <div className="text-sm text-muted-foreground">{supplier.contactTitle}</div>
              )}
              {supplier.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={`mailto:${supplier.email}`}
                    className="text-primary hover:underline"
                  >
                    {supplier.email}
                  </a>
                </div>
              )}
              {supplier.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${supplier.phone}`} className="hover:underline">
                    {supplier.phone}
                  </a>
                </div>
              )}
              {supplier.website && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={supplier.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {supplier.website}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('customers.primaryAddress')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {supplier.addressLine1 || supplier.city || supplier.country ? (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    {supplier.addressLine1 && <div>{supplier.addressLine1}</div>}
                    {supplier.addressLine2 && <div>{supplier.addressLine2}</div>}
                    <div>
                      {[supplier.city, supplier.state, supplier.postalCode]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                    {supplier.country && <div>{supplier.country}</div>}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">-</p>
              )}
              {supplier.taxId && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t('purchasing.suppliers.taxId')}
                  </span>
                  <span className="font-medium">{supplier.taxId}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('purchasing.suppliers.purchaseOrders')}
                </span>
              </div>
              <p className="mt-1 text-2xl font-bold">
                {supplier._count?.purchaseOrders ?? 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('purchasing.suppliers.products')}
                </span>
              </div>
              <p className="mt-1 text-2xl font-bold">
                {supplier._count?.supplierProducts ?? 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('purchasing.suppliers.outstandingPayables')}
                </span>
              </div>
              {openEntries.length === 0 ? (
                <p className="mt-1 text-2xl font-bold text-muted-foreground">-</p>
              ) : (
                <div className="mt-1 space-y-0.5">
                  {openEntries.map(([currency, amount]) => (
                    <p key={currency} className="text-lg font-semibold">
                      {formatCurrency(amount, currency)}
                    </p>
                  ))}
                </div>
              )}
              {overdueCount > 0 && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
                  <AlertTriangle className="h-3 w-3" />
                  {overdueCount} {t('purchasing.suppliers.overdueInvoices')}
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{tCommon('createdAt')}</span>
              </div>
              <p className="mt-1 text-lg font-medium">{formatDate(supplier.createdAt)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Brands & Products — only for T (real suppliers) */}
        {cariType === 'T' && (
          <div className="grid gap-4 lg:grid-cols-2">
            <SupplierBrandsCard
              supplierId={supplier.id}
              supplierName={supplier.companyName}
            />
            <SupplierProductsCard
              supplierId={supplier.id}
              supplierName={supplier.companyName}
            />
          </div>
        )}

        {/* Notes */}
        {supplier.notes && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('purchasing.suppliers.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap">{supplier.notes}</p>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="contacts">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('customers.tabs.contacts')}</CardTitle>
            <CardDescription>
              {(supplier.contacts?.length ?? 0)} {t('customers.tabs.contacts')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!supplier.contacts || supplier.contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <div className="space-y-3">
                {supplier.contacts.map((contact: any) => (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0"
                  >
                    <div>
                      <div className="font-medium">
                        {contact.name}
                        {contact.isPrimary && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700">
                            Primary
                          </span>
                        )}
                      </div>
                      {contact.title && (
                        <div className="text-sm text-muted-foreground">{contact.title}</div>
                      )}
                    </div>
                    <div className="text-sm text-right">
                      {contact.email && <div>{contact.email}</div>}
                      {contact.phone && (
                        <div className="text-muted-foreground">{contact.phone}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="orders" className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t('purchasing.suppliers.recentOrders')}</CardTitle>
              <CardDescription>
                {totalPOs} {t('purchasing.suppliers.purchaseOrders')}
              </CardDescription>
            </div>
            <Button variant="outline" asChild>
              <Link to={`/trading/purchasing?supplierId=${supplier.id}`}>
                {tCommon('viewAll')}
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentPOs.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <div className="space-y-2">
                {recentPOs.map((po) => (
                  <Link
                    key={po.id}
                    to={`/trading/purchasing/${po.id}`}
                    className="flex items-center justify-between p-3 border border-border hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Store className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{po.orderNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(po.orderDate)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(po.total, po.currency)}</p>
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 inline-block',
                          PO_STATUS_COLORS[po.status] ?? 'bg-gray-100 text-gray-700'
                        )}
                      >
                        {po.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="sales" className="space-y-4">
        {/* Sales orders we issued to this vendor */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t('cari.salesOrdersToVendor')}</CardTitle>
              <CardDescription>
                {salesOrdersData?.pagination?.total ?? 0} {t('cari.salesOrdersToVendor')}
              </CardDescription>
            </div>
            <Button variant="outline" asChild>
              <Link to={`/trading/sales/orders?tradingSupplierId=${supplier.id}`}>
                {tCommon('viewAll')}
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {salesOrders.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <div className="space-y-2">
                {salesOrders.map((so) => (
                  <Link
                    key={so.id}
                    to={`/trading/sales/orders/${so.id}`}
                    className="flex items-center justify-between p-3 border border-border hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Store className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{so.orderNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(so.orderDate)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(so.total, so.currency)}</p>
                      <span className="text-xs px-2 py-0.5 inline-block bg-blue-100 text-blue-700">
                        {so.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sales invoices we issued to this vendor */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t('cari.salesInvoicesToVendor')}</CardTitle>
              <CardDescription>
                {salesInvoicesData?.pagination?.total ?? 0} {t('cari.salesInvoicesToVendor')}
              </CardDescription>
            </div>
            <Button variant="outline" asChild>
              <Link to={`/trading/finance/invoices?tradingSupplierId=${supplier.id}`}>
                {tCommon('viewAll')}
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {salesInvoices.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <div className="space-y-2">
                {salesInvoices.map((inv) => (
                  <Link
                    key={inv.id}
                    to={`/trading/finance/invoices/${inv.id}`}
                    className="flex items-center justify-between p-3 border border-border hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Receipt className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{inv.invoiceNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(inv.invoiceDate)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(inv.total, inv.currency)}</p>
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 inline-block',
                          INVOICE_STATUS_COLORS[inv.status] ?? 'bg-gray-100 text-gray-700'
                        )}
                      >
                        {inv.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="invoices" className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t('purchasing.suppliers.invoices')}</CardTitle>
              <CardDescription>
                {(invoices?.length ?? 0)} {t('purchasing.suppliers.invoices')}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {!invoices || invoices.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <div className="space-y-2">
                {invoices.slice(0, 10).map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-3 border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <Receipt className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{inv.invoiceNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(inv.invoiceDate)}
                          {inv.purchaseOrderNumber && (
                            <span className="ml-2">· PO {inv.purchaseOrderNumber}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(inv.total, inv.currency)}</p>
                      <div className="flex items-center justify-end gap-2">
                        {inv.balanceDue > 0 && (
                          <span className="text-xs text-orange-600">
                            {t('customers.due')}:{' '}
                            {formatCurrency(inv.balanceDue, inv.currency)}
                          </span>
                        )}
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5',
                            INVOICE_STATUS_COLORS[inv.status] ?? 'bg-gray-100 text-gray-700'
                          )}
                        >
                          {inv.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="consignment" className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t('cari.consignmentTab')}</CardTitle>
              <CardDescription>
                {consignments.length} {t('cari.consignmentTab')}
              </CardDescription>
            </div>
            <Button variant="outline" asChild>
              <Link to={`/trading/consignment?vendorId=${supplier.id}`}>
                {tCommon('viewAll')}
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {consignments.length === 0 ? (
              <p className="text-sm text-muted-foreground">-</p>
            ) : (
              <div className="space-y-2">
                {consignments.map((c: any) => (
                  <Link
                    key={c.id}
                    to={`/trading/consignment/${c.id}`}
                    className="flex items-center justify-between p-3 border border-border hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Store className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{c.consignmentNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(c.sentDate ?? c.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs px-2 py-0.5 inline-block bg-amber-100 text-amber-700">
                        {c.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
